import React from "react";
import { useTranslation } from "react-i18next";
import { TileImage } from "@/components/common/Skeleton";
import { TIER_STYLES } from "@/components/common/SubscriberBadge";
import { MEMBER_PURPLE } from "@/components/common/VerifiedUsername";

// Shared avatar. Every surface a user's photo appears on routes through this so
// the rank ring is consistent and computed from one place. The ring is drawn from
// the SAME TIER_STYLES hex the pills use, so nothing drifts.
//
// Ring rules (mirror the backend rank_ring serializer, which is live from role +
// position + tier, so a downgrade or cancellation drops the ring on next load):
//   founding gold, pioneer silver, charter bronze  (positions 1-100)
//   don      purple metallic                        (active Don, WINS over position)
//   member (101+) and everyone else                 NO ring, by design
//
// The ring is never the only signal: rank is always shown as text in the pill next
// to the name, and the ring carries an aria-label for screen readers.
const RING_GRADIENTS = {
  founding: TIER_STYLES.founding.bg,
  pioneer: TIER_STYLES.pioneer.bg,
  charter: TIER_STYLES.charter.bg,
  don: `linear-gradient(135deg, #A78BFA, ${MEMBER_PURPLE})`,
  official: "linear-gradient(135deg, #5BA3FF, #2575FC)",
};

const RING_LABELS = {
  founding: "Founding member",
  pioneer: "Pioneer member",
  charter: "Charter member",
  don: "Don member",
  official: "Official artist",
};

export const Avatar = ({
  src,
  alt = "",
  size = 40,
  ring = null,
  className = "",
  testid = "avatar",
  onClick,
  imgClassName = "",
}) => {
  const { t } = useTranslation();
  const gradient = ring ? RING_GRADIENTS[ring] : null;
  const ringWidth = gradient ? Math.max(2, Math.round(size * 0.07)) : 0;
  const inner = size - ringWidth * 2;
  const clickable = typeof onClick === "function";

  const initial = (alt || "?").trim().charAt(0).toUpperCase() || "?";
  const photo = src ? (
    <span className="relative rounded-full overflow-hidden block" style={{ width: inner, height: inner }}>
      <TileImage
        src={src}
        alt={alt}
        testid={`${testid}-img`}
        className={`w-full h-full object-cover rounded-full ${imgClassName}`}
      />
    </span>
  ) : (
    <span
      className={`rounded-full flex items-center justify-center font-semibold ${imgClassName}`}
      style={{ width: inner, height: inner, background: "var(--bg-subtle, #e5e7eb)", color: "var(--text-secondary, #6b7280)", fontSize: Math.round(inner * 0.42) }}
      data-testid={`${testid}-fallback`}
      aria-label={alt || undefined}
    >
      {initial}
    </span>
  );

  const ringAria = gradient
    ? t(`rank.ring.${ring}`, { defaultValue: RING_LABELS[ring] })
    : undefined;

  return (
    <span
      className={`inline-flex items-center justify-center flex-shrink-0 rounded-full ${clickable ? "cursor-pointer" : ""} ${className}`}
      onClick={onClick}
      style={{
        width: size,
        height: size,
        padding: ringWidth,
        background: gradient || "transparent",
        boxShadow: gradient
          ? "inset 0 1px 1px rgba(255,255,255,0.55), inset 0 -1px 2px rgba(0,0,0,0.25), 0 1px 2px rgba(0,0,0,0.28)"
          : "none",
      }}
      role={gradient ? "img" : undefined}
      aria-label={ringAria}
      data-testid={`${testid}${ring ? `-ring-${ring}` : ""}`}
    >
      {photo}
    </span>
  );
};

export default Avatar;
